import { BuildingIndex, SurfaceIndex } from './collision.js';
import { DeckIndex } from './decks.js';
import { Heightfield } from './heightfield.js';
import { RailTraffic, type TrainBody } from './railway.js';
import type { World } from './types.js';
import { KERB_HEIGHT } from './osm/classify.js';

/**
 * Arcade car handling.
 *
 * This is deliberately not a vehicle simulation: there are no wheels, no suspension and
 * no tyre load transfer. What it does model is the two things that actually make an
 * arcade racer feel good — a yaw rate you control directly with the steering, and a
 * *separate* lateral velocity that decays at a rate you can lower on demand. When those
 * two disagree, the car is sliding, and the slide is what everyone remembers.
 *
 * Everything is integrated at a fixed timestep so the result is frame-rate independent
 * and reproducible, which is what lets the tests assert on trajectories.
 */

export const FIXED_DT = 1 / 120;

export interface CarTuning {
  /**
   * Nominal top speed on the best tarmac, m/s. The throttle tapers towards this figure
   * and the car is hard-clamped just above it, but drag settles it a little under —
   * expect roughly 0.85 × this in practice. See `drag`.
   */
  topSpeed: number;
  reverseTopSpeed: number;
  /** Peak forward acceleration, m/s², at a standstill. Tapers to zero at `topSpeed`. */
  accel: number;
  brake: number;
  /** Deceleration when nothing is pressed. */
  engineBraking: number;
  /**
   * Fraction of speed lost per second to drag at `topSpeed`, quadratic below it.
   *
   * This, not `topSpeed`, is what the car actually tops out against: the throttle tapers
   * off linearly while drag grows with the square of speed, so terminal velocity is where
   * the two cross. Raising it costs top speed far more than it costs acceleration.
   */
  drag: number;
  /**
   * Exponent on the road class's speed multiplier, so the difference between a back
   * street and a motorway is felt rather than merely measured.
   *
   * OSM's classes only span 0.86 (residential) to 1.0 (motorway), and terminal speed
   * always rises *slower* than the multiplier does — drag is quadratic, the throttle
   * taper is linear — so the raw multipliers can never separate a 115 km/h street from a
   * 155 km/h motorway. Squaring them can. 1 is the raw OSM value.
   */
  surfaceContrast: number;
  /**
   * Deceleration in m/s² applied to a car travelling faster than its surface allows.
   *
   * It exists because the obvious thing — clamping to the new surface's top speed — is
   * not deceleration at all. Off-road tops out at 9.5 m/s, so a car crossing the kerb at
   * 43 m/s lost 33 m/s inside one 1/120 s substep: about 400 g, and it felt exactly like
   * that. The ceiling is still the ceiling; the car just has to brake down to it like a
   * car would, over a second or two of ploughing through the grass.
   */
  surfaceBleed: number;
  /** Max steering rate in rad/s at the speed where the car turns best. */
  steerRate: number;
  /** Speed (m/s) at which steering authority peaks. */
  steerPeakSpeed: number;
  /** How sharply steering falls off above the peak — higher is twitchier at speed. */
  steerFalloff: number;
  /** Lateral velocity decay, per second. Higher = more grip, less slide. */
  grip: number;
  /** Grip multiplier while the handbrake is down. */
  handbrakeGrip: number;
  /** Grip multiplier on open ground; scaled by the surface's roughness. */
  offRoadGrip: number;
  /** Extra drag on open ground; scaled by the surface's roughness. */
  offRoadDrag: number;
  /**
   * Lateral speed at which the tyres let go, m/s.
   *
   * Below this the car has ordinary `grip` and behaves exactly as it always did — normal
   * cornering never gets near it. Above it, grip falls away towards `driftGrip`, which is
   * what turns a slide from something the car cancels in a third of a second into something
   * you hold with the throttle and the wheel.
   */
  breakawaySlip: number;
  /** Additional lateral speed over `breakawaySlip` at which grip has fallen all the way. */
  breakawayRange: number;
  /** Lateral decay once fully sideways. The lower this is, the longer a drift runs. */
  driftGrip: number;
  /**
   * How much of the yaw goes into lateral velocity **under braking**, on top of the
   * baseline.
   *
   * This is the deliberate half of a drift: come into the corner, turn in, and brake, and
   * the tail comes round. It was on the throttle first and that was wrong — holding
   * "forwards" is what someone does for the whole of an ordinary lap, so putting the
   * oversteer there meant the car stepped out at every corner of casual driving. Reported
   * from a phone as "it spins far too easily, even just driving along". Braking is a thing
   * you choose to do at a moment you choose; accelerating is not.
   */
  brakeOversteer: number;
  /** Grip multiplier at full braking effort on full lock. Lower is looser. */
  brakeGrip: number;
  /** Collision radius, metres. */
  radius: number;
  /** Fraction of speed kept after hitting a wall head-on. */
  wallRestitution: number;
  /** Speed lost per second of continuous wall contact, as an exponential rate. */
  wallFriction: number;
  /** How much an off-centre wall contact turns the car. 0 disables it. */
  yawOnImpact: number;
  /** How quickly the steering input eases in, per second. */
  steerLerp: number;
  /**
   * Gravity along the slope, m/s². Real gravity, deliberately.
   *
   * A 12 m DEM smoothed to kill its own lattice does not produce cliffs: the steepest thing
   * in any city we have looked at is Alfama at roughly 10%, which is 0.98 m/s² — about a
   * fifth of engine braking. That is the right order. It is enough that a long climb costs
   * you top speed and a descent hands it back, and not so much that a hill becomes a wall.
   * Exaggerating it was tempting and wrong: the terrain is real, so the physics on it should
   * be too, and the places that feel dramatic are the ones that are actually steep.
   */
  gravity: number;
  /**
   * Steepest grade the physics will honour, as rise over run.
   *
   * The terrain is real but its resolution is not: at 12 m per pixel a near-vertical river
   * embankment gets smeared across a single sample, and the steepest gradient anywhere in
   * the Kraków window comes out at 37% — a 20° slope that does not exist on the ground.
   * Any street draped across one inherits it, and an arcade car with 9 m/s² of thrust would
   * simply fail to climb a road that is flat in reality.
   *
   * 18% is a shade over the steepest street most cities have (Lombard Street is 27%, but
   * San Francisco is the outlier that proves it). Above that the slope is an artefact of
   * the sampling, and honouring it costs the player a road they can see is drivable.
   */
  maxGrade: number;
}

/**
 * Measured on a flat straight, per road class (`scripts/_tune.ts` while it existed):
 *
 *   residential  0-100 km/h  6.0 s   tops out at 121 km/h
 *   tertiary                 5.8 s              136 km/h
 *   primary                  5.7 s              152 km/h
 *   motorway                 5.7 s              157 km/h
 *   pavement                  --                 86 km/h
 *   off-road                  --                 50 km/h
 *
 * The previous values looked like a 130 km/h car and delivered 67: drag was normalised by
 * the surface top speed but not scaled down with it, so it overwhelmed the throttle taper
 * at 60% of nominal. `drag` is now an order of magnitude smaller and `accel` is lower to
 * keep 0-100 out of supercar territory.
 */
export const DEFAULT_TUNING: CarTuning = {
  topSpeed: 54,
  reverseTopSpeed: 10,
  accel: 9,
  brake: 26,
  engineBraking: 5,
  drag: 0.05,
  surfaceContrast: 2,
  // A third of the braking figure. Drag does most of the work of pulling a fast car down
  // to the grass ceiling; this only has to finish the job, and it is deliberately gentle
  // because it is the term that applies hardest at exactly the wrong moment.
  surfaceBleed: 8,
  steerRate: 2.5,
  steerPeakSpeed: 11,
  // Tuned so lateral acceleration at full lock settles around 1.3 g however fast you are
  // going: at 67 km/h the old car pulled 2.3 g, which at 150 km/h would be a spin.
  steerFalloff: 1,
  grip: 6.2,
  handbrakeGrip: 0.2,
  // Dropping a wheel off the tarmac should cost you the line, not just the speedometer.
  // With `surfaceBleed` the car now arrives on the grass still carrying real pace, so
  // this is what it does with it: understeer wide, and slide.
  offRoadGrip: 0.55,
  // Cut with `drag`, so leaving the tarmac still costs you real speed rather than acting
  // as a wall.
  offRoadDrag: 0.55,
  // 3.4 m/s sideways is 12 km/h of crab — far past anything a corner taken properly
  // produces, and about where a lift-off or a bootful of throttle puts you. Below it
  // nothing has changed; above it the car is a different, better car.
  breakawaySlip: 3.4,
  breakawayRange: 4.5,
  // A quarter of `grip`. Low enough that a slide carries through a corner on the throttle,
  // high enough that letting go of everything still gathers the car up rather than leaving
  // it spinning down the street.
  driftGrip: 2.2,
  brakeOversteer: 1.2,
  // Between `grip` and the handbrake's 0.2: braking into a corner should let the back go,
  // but the handbrake has to stay the bigger hammer.
  brakeGrip: 0.35,
  // Half the car's 1.86 m width. See BODY_OFFSETS: the body is three of these along the
  // car's spine, not one circle at its centre.
  radius: 0.95,
  wallRestitution: 0.3,
  wallFriction: 1.4,
  steerLerp: 9,
  yawOnImpact: 0.5,
  gravity: 9.81,
  maxGrade: 0.18,
};

/**
 * The collision body: three circles at these offsets along the car's spine, in metres,
 * measured from the centre with positive towards the nose.
 *
 * A car cannot be one circle. Size that circle to the half-length and it is absurdly fat
 * sideways and catches on corners it visibly clears; size it to the half-width — which is
 * what this used to be — and the ends are unprotected, so the nose buries itself most of a
 * metre into a wall before anything registers. Three width-sized circles down the middle
 * give a body that is the right shape in both axes for the cost of two extra broadphase
 * queries per substep.
 *
 * ±1.06 m with a 0.95 m radius puts the outer edges at ±2.01 m, a shade proud of the Ford
 * Ka's own 1.97 m half-length — and 0.95 is likewise a shade over its 0.93 m half-width.
 * That is deliberate. A body sized exactly to the mesh leaves the paint touching a wall at
 * the moment of contact, and `resolveCircle` reports nothing at exactly contact distance,
 * so the visible tip of the nose ends up a hair inside the facade. Four centimetres of
 * margin is far below anything a player can see and makes "the car is never inside a
 * building" true rather than approximately true.
 *
 * The one thing this shape cannot do is reach the four corners. Circles on the centre line
 * with a radius of the half-width leave a corner overhang of (√2 − 1) × half-width ≈ 0.39 m
 * whatever their number or spacing, so a car jammed nose-first into a facade at an angle
 * shows a corner of its bumper inside it. Closing that means circles off the spine, or a
 * box, and a body that protected the corners would snag on ones the car visibly clears —
 * which is the thing the collision radius has always traded away on purpose.
 */
const BODY_OFFSETS = [1.06, 0, -1.06] as const;

/**
 * Steering lock below which braking produces no extra tail-out at all.
 *
 * Braking in a straight line is braking, and has to stay braking — you do it at the end of
 * every straight and it must not put the car sideways. Braking at four tenths of lock or
 * more is somebody asking the back to come round.
 */
const OVERSTEER_LOCK = 0.4;

/**
 * Forward speed below which braking never induces a slide, m/s.
 *
 * Negative throttle is also how the car reverses, and a car being reversed out of a wall at
 * walking pace has no business breaking traction.
 */
const OVERSTEER_MIN_SPEED = 4;

/** How much stopping power a fully-sliding brake gives up, as a fraction. */
const BRAKE_SLIDE_STOPPING_LOSS = 0.45;

/** Distance from the centre to the outermost circle, for scaling the yaw a contact induces. */
const BODY_SPREAD = Math.max(...BODY_OFFSETS.map(Math.abs));

/**
 * Car-to-car contact, tuned against two cars only — no building, no road, nothing else
 * involved — so these numbers are about what a collision *feels* like, not about geometry.
 *
 * Both came out of a throwaway two-car harness run under `tsx` (not committed — it
 * imported this file directly and printed a trajectory, the same kind of thing
 * `scripts/_tune.ts` was for the straight-line figures above), plus one failure that only
 * showed up against the real `Lobby.stepAi` loop with a full grid of AI on a real fixture
 * — see the note on why there is no velocity impulse below, which is the more important of
 * the two things this section says.
 *
 * **There is deliberately no velocity response to a car-car contact, only a position one.**
 * The first version had both: push the overlap out, and remove some of the closing
 * velocity along the contact normal, the same shape as the wall response below. It passed
 * every scenario in the harness — a clean head-on stop, a gentle graze that lost almost no
 * speed — and then produced an eight-to-ten-second pile-up at the start of every AI grid
 * race, three opponents parked nose to tail a few metres off the line while their speed
 * oscillated and their progress went nowhere (`aiRace.test.ts`'s finish-order test is what
 * caught it). The cause: a driver has no idea it is touching another car, so it keeps
 * steering back at the same point on the racing line every tick, and if that point is
 * genuinely contested — which the first few metres off a packed grid often is — contact
 * re-fires on nearly every substep for seconds at a time. A wall-style impulse removes
 * real velocity on every one of those substeps; the engine's own `accel` only replaces
 * about 0.075 m/s of it per substep, so the impulse wins every time and the car can never
 * build up the speed to clear the pinch point. Turning the impulse off and re-running the
 * same grid produced clean, fast breaks for all three opponents inside four seconds —
 * confirmed by disabling it in isolation first, not by removing it and hoping. What is left
 * still stops a head-on pair without letting their bodies overlap (`CAR_CONTACT_PUSH_MAX`
 * is sized for that) — it is just `engineBraking` and drag doing the stopping, gradually,
 * the same as it would with the throttle simply released, rather than a wall-style impulse
 * doing it in one hit. That is slower for a hard head-on (about 1.8 s to rest instead of
 * 0.2 s, measured) and it is the right trade: DECISIONS.md §19 made exactly this argument
 * about leaving the road — a deceleration, not a clamp — and a car is not the ground, so it
 * gets even less claim to an instant one.
 */

/**
 * Cap on how far one substep may shove a car to resolve a car-car overlap, metres.
 *
 * With no velocity response (see above), this is now the only thing standing between a
 * head-on pair and an overlapping body — the ceiling has to be high enough to hold two cars
 * closing at 30 m/s combined apart using position correction alone. 0.05 m, the first value
 * tried, was not: the harness's head-on scenario ended each step 0.07 m short of contact
 * distance, a real but small overlap. 0.09 m held it with room to spare, and 0.1 m is that
 * plus a margin. It is still far short of "uncapped": the harness's two-metre stale-overlap
 * scenario (standing in for two 20 Hz snapshots that briefly disagree about where a car is)
 * resolves uncapped in a single 1/120 s substep — 1.9 m in one step, measured, 228 m/s of
 * implied motion, the exact shape of the `surfaceBleed` bug this project already paid to
 * fix once. At 0.1 m the same overlap clears over about a fifth of a second of substeps —
 * visibly a shove, never a jump.
 */
const CAR_CONTACT_PUSH_MAX = 0.1;

/**
 * How much an off-centre car-car contact turns the car, against `yawOnImpact`'s default of
 * 0.5 for a wall.
 *
 * Tried the wall's own figure first, on a graze in the harness (side by side at 25 m/s,
 * drifting together at 0.3 m/s). At 0.5 the worst single substep turns the car 0.030 rad —
 * 3.6 rad/s, a wheel flick — and the heading trace does not settle cleanly, it overshoots
 * and corrects, because a car has nothing rigid to pivot against the way a wall does and
 * 0.5 was tuned for a pivot that exists here only symmetrically, shared between two cars
 * each getting shoved by the same contact. 0.16 halves the worst single-substep turn to
 * 0.016 rad and settles without the overshoot — reads as pushed wide, not spun.
 */
const CAR_CONTACT_YAW = 0.16;

/**
 * What a car-car contact does to speed: once, when it starts.
 *
 * The position response above is still the whole of what holds two cars apart, and still has
 * no velocity term, for the grid pile-up reason given there. But a crash that costs nothing
 * read as two ghosts brushing past each other — "at least add a slowdown or a sound of
 * crash", from playing it. So the first substep of a contact, and only that one, reports the
 * closing speed as an impact (which is what the crash sounds already listen to) and takes
 * some speed off: the part of this car's velocity driving into the other, mostly, and a
 * share of the rest scaled by how hard the hit was. Then nothing more until the cars have
 * been apart for `cooldown` seconds, so a pinch point on a packed grid is one bump, not a
 * hundred and twenty a second — which is precisely what the old impulse got wrong.
 *
 * - `absorb`: the fraction of this car's own velocity into the other that it loses.
 * - `scrubPerMs`, `scrubMax`: overall speed lost, per m/s of closing speed and at most.
 */
const BUMP = { absorb: 0.6, scrubPerMs: 0.02, scrubMax: 0.3, cooldown: 0.6 };

/**
 * What a kerb does to a car that goes over it. DECISIONS.md §93.
 *
 * The pavement has cost speed since §43, but nothing marked the moment of getting onto it: a
 * car crossed sixteen centimetres of granite as if it were paint, and then drove along the
 * pavement *inside* the slab, because the car stood on the ground and the slab is drawn above
 * it. "It doesn't seem like it has any collisions" was exactly right.
 *
 * Not a wall — §19 is the house rule, and a kerb is something a car can mount, at a price. So
 * on the substep the car gets up onto a pavement, the speed it is carrying across the kerb is
 * the closing speed, and it loses a share of that (`absorb`) plus a share of its whole speed by
 * how hard it went at it (`scrubPerMs`, at most `scrubMax`): running wide at 30 m/s and 2 m/s
 * across costs about 6%, and going straight at it at 10 m/s costs more than half. Coming back
 * down costs nothing. Both report the speed across as `kerb`, which the client plays as a
 * thump. While up there the car rides on top of the slab rather than inside it.
 */
const KERB = { absorb: 0.4, scrubPerMs: 0.015, scrubMax: 0.15, dropShare: 0.5 };

/**
 * Closest points between two segments in the car's XZ plane — the standard construction
 * (Ericson, *Real-Time Collision Detection* §5.1.9), specialised to 2D. Returns `s`, the
 * parameter along the first segment (0 at `p1`, 1 at `p2`), alongside both closest points.
 *
 * This is what a car's body actually is for contact purposes: not three discrete circles
 * but the capsule they approximate — the spine from tail to nose, radius `t.radius`. A
 * first cut tested the three `BODY_OFFSETS` circles against each other's three, picked
 * whichever pair overlapped most, and it broke exactly where a capsule cannot: two bodies
 * spawned almost exactly on top of each other (10 cm apart, which a bad grid slot or a
 * same-tick respawn could produce) have several circle pairs overlapping at once, on both
 * sides of centre, and picking a push direction per own-circle from whichever opposing
 * circle it happened to be nearest to gave the front and the tail *opposite* answers —
 * front says "push north", tail says "push south", and the averaged result barely moved
 * the car despite it being almost entirely inside the other one. A capsule has no such
 * ambiguity: there is exactly one pair of closest points between two segments, however
 * deep or symmetric the overlap, so there is exactly one push direction.
 */
function closestSegmentPoints(
  p1x: number,
  p1z: number,
  p2x: number,
  p2z: number,
  q1x: number,
  q1z: number,
  q2x: number,
  q2z: number,
): { s: number; ax: number; az: number; bx: number; bz: number } {
  const d1x = p2x - p1x;
  const d1z = p2z - p1z;
  const d2x = q2x - q1x;
  const d2z = q2z - q1z;
  const rx = p1x - q1x;
  const rz = p1z - q1z;
  const a = d1x * d1x + d1z * d1z;
  const e = d2x * d2x + d2z * d2z;
  const f = d2x * rx + d2z * rz;

  const EPS = 1e-9;
  let s = 0;
  let u = 0;
  if (a <= EPS && e <= EPS) {
    s = 0;
    u = 0;
  } else if (a <= EPS) {
    u = clamp(f / e, 0, 1);
  } else {
    const c = d1x * rx + d1z * rz;
    if (e <= EPS) {
      s = clamp(-c / a, 0, 1);
    } else {
      const b = d1x * d2x + d1z * d2z;
      const denom = a * e - b * b;
      s = denom !== 0 ? clamp((b * f - c * e) / denom, 0, 1) : 0;
      u = (b * s + f) / e;
      if (u < 0) {
        u = 0;
        s = clamp(-c / a, 0, 1);
      } else if (u > 1) {
        u = 1;
        s = clamp((b - c) / a, 0, 1);
      }
    }
  }

  return { s, ax: p1x + d1x * s, az: p1z + d1z * s, bx: q1x + d2x * u, bz: q1z + d2z * u };
}

export interface CarInput {
  /** -1..1, positive accelerates. */
  throttle: number;
  /** -1..1, positive steers left (counter-clockwise seen from above). */
  steer: number;
  handbrake: boolean;
}

export const NEUTRAL_INPUT: CarInput = { throttle: 0, steer: 0, handbrake: false };

export interface CarState {
  x: number;
  z: number;
  /**
   * Ground height under the car, metres. An output, not an input: the car is still a 2D
   * simulation driving on a draped surface, which is the right model for an arcade racer —
   * it cannot leave the ground, so there is nothing for a third axis of state to hold.
   */
  y: number;
  /** Radians. 0 is due north; see `headingFromDirection`. */
  heading: number;
  /** Velocity in world metres/second. */
  vx: number;
  vz: number;
  /** Smoothed steering, so a keyboard's on/off input doesn't snap the wheels. */
  steer: number;
  /** Read-only outputs, refreshed every step for the HUD and effects. */
  speed: number;
  onRoad: boolean;
  /** Lateral speed magnitude — drives the skid marks and the tyre squeal. */
  slip: number;
  /** Past the breakaway point and moving: a slide being held, not a twitch. */
  drifting: boolean;
  /** Impact speed of the last collision this step — a wall or another car — 0 if none. */
  impact: number;
  /** Up on a pavement — see `KERB`. Also what lifts `y` by the kerb's height. */
  pavement: boolean;
  /** Speed across the kerb if the car went over one since the last call, up or down; else 0. */
  kerb: number;
  /**
   * Seconds until a touch with another car may knock speed off again. Held at its full
   * value for as long as the cars stay in contact, so a bump is one event — see `BUMP`.
   */
  bumpCooldown: number;
}

export function createCarState(x: number, z: number, heading: number): CarState {
  return {
    x,
    z,
    y: 0,
    heading,
    vx: 0,
    vz: 0,
    steer: 0,
    speed: 0,
    onRoad: true,
    slip: 0,
    drifting: false,
    impact: 0,
    pavement: false,
    kerb: 0,
    bumpCooldown: 0,
  };
}

/**
 * Another car's pose, as far as contact resolution needs to know it.
 *
 * Deliberately just this. It is not a `CarState` — a client has no `CarState` for a remote,
 * only an interpolated position, and the server's AI loop would otherwise have to fake
 * velocity and steer for humans it never simulates. Nobody who supplies this list is
 * claiming to know how fast the other car is going, only where it currently appears to be.
 */
export interface OtherCar {
  x: number;
  z: number;
  heading: number;
  /**
   * Forward speed as last reported, m/s, signed — what the snapshot stream carries anyway.
   * Only used to size a bump (see `BUMP`), never to move this car along with the other: a
   * car parked in the road and one doing 50 are different crashes to drive into.
   */
  speed?: number;
}

export interface CarWorld {
  surface: SurfaceIndex;
  buildings: BuildingIndex;
  /**
   * The terrain. Optional because most regions are flat, every fixture is, and the game has
   * to run when the DEM could not be fetched — `Heightfield.flat()` is the fallback and it
   * costs a branch.
   */
  ground?: Heightfield;
  /**
   * The bridges and tunnels, which the terrain does not describe.
   *
   * Without this the car is placed at ground height everywhere, and a bridge is precisely
   * the place where the ground is not the road: crossing the Vistula it drove three metres
   * under Most Grunwaldzki's deck, and crossing the Seine it drove under the river as well.
   * Optional for the same reason `ground` is — a flat world has no decks to be on.
   */
  decks?: DeckIndex;
  /**
   * Everyone else on the grid, for car-to-car contact. Optional, and absent by default —
   * `carWorldFor` never sets it, because there is no single owner of "everyone's position"
   * the way there is for buildings. Callers that know who else is racing (`Game.frame` on
   * the client, `Lobby.stepAi` on the server) attach it themselves, fresh, every step.
   *
   * See the contact section of `substep` for what "collides" means here: only the car
   * being stepped ever moves. Nothing in this list is ever displaced, which is what makes
   * it safe for a client to pass in cars it does not own.
   */
  cars?: OtherCar[];
  /**
   * The railway's timetable, built with the rest of the world. It is a function of time, so
   * the physics does not ask it anything: callers who know the clock turn it into `trains`.
   */
  rail?: RailTraffic;
  /**
   * The rolling stock at this moment, as boxes — from `rail.bodiesAt(serverSeconds)`, attached
   * fresh by the same callers that attach `cars`. Solid, and moving: a train that runs into a
   * car carries it along. See DECISIONS.md §95.
   */
  trains?: TrainBody[];
}

/**
 * Everything the physics needs to know about a world, built from the world itself.
 *
 * It exists because assembling a `CarWorld` by hand is a bug waiting to happen: the terrain
 * arrived after the other two indexes did, and every call site that had been writing
 * `{ surface, buildings }` went on compiling and went on quietly driving flat physics
 * through a city with hills in it. One constructor means a new field can never again be
 * silently omitted by everything that was written before it existed.
 */
export function carWorldFor(world: World): CarWorld {
  const ground = world.ground ? Heightfield.from(world.ground) : Heightfield.flat();
  const decks = DeckIndex.build(world, ground);
  return {
    surface: new SurfaceIndex(world),
    buildings: new BuildingIndex(world),
    ground,
    decks,
    ...(world.rail ? { rail: RailTraffic.build(world, { terrain: ground, decks }) } : {}),
  };
}

/**
 * Most fixed steps one call may run, however long the frame was: 100 ms of driving.
 *
 * A tabbed-out browser comes back with seconds on its clock, and simulating all of it at
 * once would teleport the car. Time past this is dropped, not owed — which is the one
 * place a client can lose time, and it only happens below 10 fps.
 */
export const MAX_FIXED_STEPS = 12;

/**
 * Advances the car by `dt` seconds of fixed substeps, for callers whose `dt` is already a
 * whole number of `FIXED_DT` — the server's AI at 60 Hz, and the tests.
 *
 * Anything driven by a real clock must not call this. It rounds `dt` to whole steps and
 * forgets the rest, which once cost nothing worth mentioning per call and turned out to
 * cost a constant fraction of every frame: 144 fps rounded 6.9 ms up to 8.3 and drove 20 %
 * fast, 100 fps rounded 10 ms down and drove 17 % slow. A frame loop holds a `FixedClock`,
 * which carries the remainder, and calls `stepCarFixed`. See DECISIONS.md §59.
 */
export function stepCar(
  state: CarState,
  input: CarInput,
  world: CarWorld,
  dt: number,
  tuning: CarTuning = DEFAULT_TUNING,
): void {
  const steps = Math.min(MAX_FIXED_STEPS, Math.max(1, Math.round(dt / FIXED_DT)));
  stepCarFixed(state, input, world, steps, tuning);
}

/** Where a car is, for drawing it between two physics steps. */
export interface CarPose {
  x: number;
  y: number;
  z: number;
  heading: number;
}

export function carPose(state: CarState): CarPose {
  return { x: state.x, y: state.y, z: state.z, heading: state.heading };
}

/**
 * Runs exactly `steps` fixed substeps, which may be none.
 *
 * `previous`, when given, is overwritten with the pose before the last step that ran, so a
 * renderer can draw the car partway between the two. Untouched when `steps` is zero — the
 * pair it already holds is still the pair the car is between.
 *
 * `impact` is cleared even when no step runs: it means "hit something since the last call",
 * and a 144 Hz frame that ran no physics must not hand the previous frame's crash to the
 * audio a second time.
 */
export function stepCarFixed(
  state: CarState,
  input: CarInput,
  world: CarWorld,
  steps: number,
  tuning: CarTuning = DEFAULT_TUNING,
  previous?: CarPose,
): void {
  state.impact = 0;
  state.kerb = 0;
  for (let i = 0; i < steps; i++) {
    if (previous && i === steps - 1) {
      previous.x = state.x;
      previous.y = state.y;
      previous.z = state.z;
      previous.heading = state.heading;
    }
    substep(state, input, world, FIXED_DT, tuning);
  }
}

/**
 * Real time in, whole physics steps out, with the remainder kept for next frame.
 *
 * The remainder is the whole point: the car covers exactly as much simulated time as the
 * wall clock did, at any frame rate, so two players on different monitors are driving the
 * same car. `alpha` is how far into the next step the clock already is, which is what a
 * renderer interpolates by.
 */
export class FixedClock {
  private debt = 0;

  /** Adds `dt` seconds and returns how many fixed steps are now due. */
  advance(dt: number): number {
    this.debt = Math.min(this.debt + Math.max(0, dt), MAX_FIXED_STEPS * FIXED_DT);
    // The epsilon is for the remainder floating point leaves behind: a debt that should be
    // exactly two steps can come out a hair under, and flooring that runs one step a frame
    // late and two the frame after — a stutter at precisely the frame rate that had none.
    const steps = Math.floor(this.debt / FIXED_DT + 1e-6);
    this.debt = Math.max(0, this.debt - steps * FIXED_DT);
    return steps;
  }

  /** Fraction of a step accumulated but not yet simulated, 0 to 1. */
  get alpha(): number {
    return Math.min(1, this.debt / FIXED_DT);
  }

  reset(): void {
    this.debt = 0;
  }
}

/**
 * The pose to draw: `alpha` of the way from `from` to `to`, written into `out`.
 *
 * Shortest-arc on the heading, so a car pointing across the ±PI seam does not spin.
 */
export function lerpPose(from: CarPose, to: CarPose, alpha: number, out: CarPose): CarPose {
  out.x = from.x + (to.x - from.x) * alpha;
  out.y = from.y + (to.y - from.y) * alpha;
  out.z = from.z + (to.z - from.z) * alpha;
  let dh = (to.heading - from.heading) % (Math.PI * 2);
  if (dh > Math.PI) dh -= Math.PI * 2;
  if (dh <= -Math.PI) dh += Math.PI * 2;
  out.heading = from.heading + dh * alpha;
  return out;
}

function substep(
  s: CarState,
  input: CarInput,
  world: CarWorld,
  dt: number,
  t: CarTuning,
): void {
  const surface = world.surface.sample(s.x, s.z);
  const wasRoad = s.onRoad;
  const wasPavement = s.pavement;
  s.onRoad = surface.onRoad;
  s.pavement = surface.pavement;
  const ground = world.ground;

  // --- the kerb -----------------------------------------------------------
  // Up from the carriageway or from the grass behind, or back down onto the carriageway. Off
  // the back of a pavement onto open ground is a step down with nothing to strike.
  if (surface.pavement !== wasPavement && (surface.pavement || surface.onRoad !== wasRoad)) {
    const across = Math.abs(s.vx * surface.nx + s.vz * surface.nz);
    if (surface.pavement) {
      // Speed, not direction: knocking the velocity sideways put a car that ran wide in a
      // drift, which is a second penalty for one mistake and one the player did not ask for.
      const speed = Math.hypot(s.vx, s.vz);
      if (speed > 1e-6) {
        const lost = across * KERB.absorb + speed * Math.min(KERB.scrubMax, across * KERB.scrubPerMs);
        const keep = Math.max(0, speed - lost) / speed;
        s.vx *= keep;
        s.vz *= keep;
      }
      s.kerb = Math.max(s.kerb, across);
    } else {
      s.kerb = Math.max(s.kerb, across * KERB.dropShare);
    }
  }

  // Forward / right basis for the current heading. Forward is local -Z.
  const fx = -Math.sin(s.heading);
  const fz = -Math.cos(s.heading);
  const rx = -fz;
  const rz = fx;

  let vLong = s.vx * fx + s.vz * fz;
  let vLat = s.vx * rx + s.vz * rz;

  // --- longitudinal -------------------------------------------------------
  const topSpeed = t.topSpeed * Math.pow(surface.speed, t.surfaceContrast);
  const throttle = clamp(input.throttle, -1, 1);

  // Braking hard with the wheel turned, at a speed worth doing it at: the one input that
  // means "put the back out". Computed here rather than down in the grip section because
  // the longitudinal pass needs it too — a car whose rear has let go does not brake as
  // well as one whose tyres are gripping, and without that the brake simply stopped the
  // car before any slide could develop. `s.steer` is last substep's smoothed value, which
  // at 120 Hz is the same number to three decimal places.
  const brakeSlide =
    vLong > OVERSTEER_MIN_SPEED
      ? Math.max(0, -throttle) *
        (Math.max(0, Math.abs(s.steer) - OVERSTEER_LOCK) / (1 - OVERSTEER_LOCK))
      : 0;

  if (throttle > 0.01) {
    // Acceleration tapers to zero as you approach the surface's top speed, which gives
    // a natural-feeling power curve without modelling gears.
    const headroom = clamp(1 - vLong / Math.max(1, topSpeed), 0, 1);
    vLong += t.accel * throttle * headroom * dt;
  } else if (throttle < -0.01) {
    if (vLong > 0.5) {
      // Sliding tyres stop a car less well than gripping ones. This is also what makes the
      // drift reachable at all: at full braking effort the car sheds 26 m/s² and is down to
      // walking pace inside a second, which is far too quick for a slide to build.
      vLong += t.brake * throttle * dt * (1 - BRAKE_SLIDE_STOPPING_LOSS * brakeSlide);
    } else {
      const headroom = clamp(1 + vLong / t.reverseTopSpeed, 0, 1);
      vLong += t.accel * 0.7 * throttle * headroom * dt;
    }
  } else {
    const decel = t.engineBraking * dt;
    vLong = vLong > 0 ? Math.max(0, vLong - decel) : Math.min(0, vLong + decel);
  }

  if (input.handbrake) vLong -= Math.sign(vLong) * Math.min(Math.abs(vLong), 9 * dt);

  // Ordinary drag is normalised against *this surface's* ceiling, which is what makes
  // terminal speed track the road class. The off-road penalty is not: normalising it the
  // same way divides by the 9.5 m/s grass ceiling instead of the car's 54, which made it
  // almost six times fiercer than motorway drag and produced 139 m/s² the instant a wheel
  // left the tarmac. Grass should be a surface, not a crash barrier.
  let dragAccel = vLong * (t.drag / Math.max(8, topSpeed)) * Math.abs(vLong);
  if (surface.roughness > 0) {
    // Scaled by roughness, so a paving slab costs a fraction of what a field costs. A kerb
    // and a park used to be the same surface, which is what made clipping a corner feel
    // like a punishment rather than a mistake.
    dragAccel +=
      surface.roughness * vLong * (t.offRoadDrag / Math.max(8, t.topSpeed)) * Math.abs(vLong);
  }
  vLong -= dragAccel * dt;

  // Speed above what this surface allows is bled off, not clipped. Under power the
  // throttle taper means the car never gets here at all; it only matters the moment the
  // ceiling drops out from under you, which is to say every time you leave the road.
  //
  // A flat rate rather than a proportional one, because proportional scales with how far
  // over the ceiling you are — and the case that matters most, arriving at 156 km/h, is
  // exactly the case where that is largest. Drag already punishes speed quadratically;
  // this only has to stop the last of it hanging around.
  const ceiling = topSpeed * 1.02;
  if (vLong > ceiling) vLong = Math.max(ceiling, vLong - t.surfaceBleed * dt);
  vLong = Math.max(vLong, -t.reverseTopSpeed);

  // --- gravity along the slope --------------------------------------------
  // Applied after the ceiling rather than before it, so a descent can actually carry the
  // car past what the surface would otherwise allow — freewheeling down a hill faster than
  // you could drive up it is the entire point of having hills.
  if (ground && !ground.isFlat) {
    // A deck's own gradient, where there is one. The riverbank the DEM has under a bridge
    // is nothing the car on the bridge can feel, and in Le Marais it is a 15 m wall: taking
    // it would brake a car halfway across the Seine and then fire it off the far side.
    const deck = world.decks?.sample(s.x, s.z, s.y);
    const g = deck ? { dx: deck.gradeX, dz: deck.gradeZ } : ground.slopeAt(s.x, s.z);
    // `slopeAt` is uphill-positive, so pointing up it is a positive dot product and has to
    // subtract speed. The gradient is a tangent; dividing by the length of the slope vector
    // converts it to the sine, which stops the pull growing without bound on a cliff edge.
    const grade = clamp(g.dx * fx + g.dz * fz, -t.maxGrade, t.maxGrade);
    vLong -= ((t.gravity * grade) / Math.sqrt(1 + grade * grade)) * dt;
  }

  // --- steering -----------------------------------------------------------
  const targetSteer = clamp(input.steer, -1, 1);
  s.steer += (targetSteer - s.steer) * Math.min(1, t.steerLerp * dt);

  const speedAbs = Math.abs(vLong);
  // Steering authority ramps in from a standstill, peaks, then tapers so the car is
  // stable flat out but still rotates hard through a 30 km/h hairpin.
  const ramp = clamp(speedAbs / 3.2, 0, 1);
  const taper =
    speedAbs <= t.steerPeakSpeed
      ? 1
      : 1 / (1 + ((speedAbs - t.steerPeakSpeed) / t.steerPeakSpeed) * t.steerFalloff * 2.2);
  const yawRate = s.steer * t.steerRate * ramp * taper * (vLong < -0.2 ? -1 : 1);
  s.heading += yawRate * dt;

  // --- lateral grip -------------------------------------------------------
  let grip = t.grip;
  if (input.handbrake) grip *= t.handbrakeGrip;
  // Braking into a corner unloads the rear. Feeding lateral velocity in was not enough on
  // its own — grip scrubbed it off as fast as it arrived — so the brake lowers the grip
  // that is doing the scrubbing, the same way the handbrake does but less brutally.
  if (brakeSlide > 0) grip *= 1 - (1 - t.brakeGrip) * brakeSlide;
  if (surface.roughness > 0) grip *= 1 - (1 - t.offRoadGrip) * surface.roughness;

  // Some of the yaw is fed into lateral velocity so the back steps out instead of the car
  // turning on the spot — and much more of it under braking, which is what lets a corner be
  // taken sideways on purpose without reaching for the handbrake.
  //
  // Gated on three things, and all three earn their place. On **braking** rather than
  // throttle, because holding "forwards" is what ordinary driving is and a car that steps
  // out every time you accelerate through a bend is not fun, it is twitchy. On **steering
  // lock**, so braking down a straight stays braking. And on **speed**, because negative
  // throttle is also reverse.
  vLat += yawRate * vLong * dt * (0.55 + t.brakeOversteer * brakeSlide);

  // Past the breakaway point the tyres are over their peak and grip falls away, so a slide
  // sustains itself instead of being cancelled in a third of a second. This is the whole of
  // the drift: below `breakawaySlip` the car is exactly the car it was, and above it the
  // slide is something you hold with the wheel and the throttle rather than something that
  // happens to you briefly. `min` rather than a plain interpolation, so this can only ever
  // *reduce* grip — on grass or with the handbrake down it is already lower than `driftGrip`
  // and must not be quietly raised back up to it.
  const slide = clamp((Math.abs(vLat) - t.breakawaySlip) / t.breakawayRange, 0, 1);
  if (slide > 0) grip += (Math.min(grip, t.driftGrip) - grip) * slide;

  vLat *= Math.exp(-grip * dt);
  s.slip = Math.abs(vLat);
  s.drifting = slide > 0.25 && Math.abs(vLong) > 5;

  // --- integrate ----------------------------------------------------------
  const nfx = -Math.sin(s.heading);
  const nfz = -Math.cos(s.heading);
  const nrx = -nfz;
  const nrz = nfx;
  s.vx = nfx * vLong + nrx * vLat;
  s.vz = nfz * vLong + nrz * vLat;
  s.x += s.vx * dt;
  s.z += s.vz * dt;

  // --- other cars -----------------------------------------------------------
  // Resolved before the wall pass below, deliberately: a shove away from another car must
  // never win an argument with a building. Buildings running last means anything this
  // section does gets corrected again if it happened to land the car in a facade.
  //
  // Only ever moves *this* car. `world.cars` is somebody else's reported or interpolated
  // position, not a `CarState` this function owns, so there is nothing here to write back
  // into. Two cars trading paint therefore do not resolve the same contact once — each
  // side separately shoves itself clear of wherever it currently believes the other one
  // is, which is not identical for both of them (one may be running against a fresher
  // position than the other) but does mean neither ends the step still overlapping.
  if (world.cars && world.cars.length > 0) {
    // Own spine, tail to nose — the capsule this car's body approximates for contact
    // purposes. Built once per substep rather than once per other car; only the direction
    // changes with heading, not with who else is nearby.
    const p1x = s.x - nfx * BODY_SPREAD;
    const p1z = s.z - nfz * BODY_SPREAD;
    const p2x = s.x + nfx * BODY_SPREAD;
    const p2z = s.z + nfz * BODY_SPREAD;
    const minDist = 2 * t.radius;
    // Broadphase on centres, before the segment maths: two cars whose centres are farther
    // apart than both spines' full reach cannot touch anywhere along them, and in ordinary
    // racing that is nearly always true — this is what keeps a full grid cheap.
    const reach = 2 * BODY_SPREAD + minDist + 0.5;

    let ccPushX = 0;
    let ccPushZ = 0;
    let ccYaw = 0;
    let ccContacts = 0;
    // The hardest of this substep's contacts: its closing speed and normal, for `BUMP`.
    let bumpClosing = 0;
    let bumpNx = 0;
    let bumpNz = 0;

    for (const other of world.cars) {
      const ddx = s.x - other.x;
      const ddz = s.z - other.z;
      if (ddx * ddx + ddz * ddz > reach * reach) continue;

      const ofx = -Math.sin(other.heading);
      const ofz = -Math.cos(other.heading);
      const q1x = other.x - ofx * BODY_SPREAD;
      const q1z = other.z - ofz * BODY_SPREAD;
      const q2x = other.x + ofx * BODY_SPREAD;
      const q2z = other.z + ofz * BODY_SPREAD;

      const c = closestSegmentPoints(p1x, p1z, p2x, p2z, q1x, q1z, q2x, q2z);
      const dx = c.ax - c.bx;
      const dz = c.az - c.bz;
      const dist = Math.hypot(dx, dz);
      if (dist >= minDist) continue;

      const pen = minDist - dist;
      let nx: number;
      let nz: number;
      if (dist > 1e-4) {
        nx = dx / dist;
        nz = dz / dist;
      } else {
        // Coincident closest points — two spines crossing exactly, or two cars occupying
        // the same point. Fall back to the centre-to-centre direction, which is never
        // zero once the broadphase above has already required the centres to differ by
        // more than a rounding error in the general case; deterministic either way.
        const cLen = Math.hypot(ddx, ddz);
        nx = cLen > 1e-6 ? ddx / cLen : 1;
        nz = cLen > 1e-6 ? ddz / cLen : 0;
      }

      ccContacts++;
      // How fast the two are coming together along the normal, from this car's velocity and
      // the other's reported speed along its heading.
      const otherSpeed = other.speed ?? 0;
      const closing = -((s.vx - ofx * otherSpeed) * nx + (s.vz - ofz * otherSpeed) * nz);
      if (closing > bumpClosing) {
        bumpClosing = closing;
        bumpNx = nx;
        bumpNz = nz;
      }
      const px = nx * pen;
      const pz = nz * pen;
      ccPushX += px;
      ccPushZ += pz;
      // Signed distance of the contact point from this car's own centre, along its spine —
      // 0 at the middle, ±BODY_SPREAD at the ends. The capsule equivalent of "which of the
      // three circles took the hit" for the wall code's yaw term.
      const offset = (c.s - 0.5) * 2 * BODY_SPREAD;
      if (Math.abs(offset) > 1e-3) {
        const lateral = px * nrx + pz * nrz;
        ccYaw -= lateral * (offset / BODY_SPREAD) * CAR_CONTACT_YAW;
      }
    }

    if (ccContacts > 0) {
      // Position only — no velocity response. See the comment on `CAR_CONTACT_PUSH_MAX`'s
      // section above for the AI grid-start pile-up this avoided, and DECISIONS.md for the
      // full write-up.
      const rawX = ccPushX / ccContacts;
      const rawZ = ccPushZ / ccContacts;
      const rawLen = Math.hypot(rawX, rawZ);
      if (rawLen > CAR_CONTACT_PUSH_MAX) {
        s.x += (rawX / rawLen) * CAR_CONTACT_PUSH_MAX;
        s.z += (rawZ / rawLen) * CAR_CONTACT_PUSH_MAX;
      } else {
        s.x += rawX;
        s.z += rawZ;
      }
      s.heading += clamp(ccYaw / ccContacts, -0.03, 0.03);

      if (s.bumpCooldown <= 0 && bumpClosing > 0) {
        s.impact = Math.max(s.impact, bumpClosing);
        const into = s.vx * bumpNx + s.vz * bumpNz;
        if (into < 0) {
          s.vx -= into * BUMP.absorb * bumpNx;
          s.vz -= into * BUMP.absorb * bumpNz;
        }
        const keep = 1 - Math.min(BUMP.scrubMax, bumpClosing * BUMP.scrubPerMs);
        s.vx *= keep;
        s.vz *= keep;
      }
      s.bumpCooldown = BUMP.cooldown;
    } else {
      s.bumpCooldown = Math.max(0, s.bumpCooldown - dt);
    }
  } else {
    s.bumpCooldown = Math.max(0, s.bumpCooldown - dt);
  }

  // --- walls --------------------------------------------------------------
  // Every circle is tested against the car's position *before* any correction, and the
  // corrections are combined and applied once. Resolving them one at a time and moving
  // the car in between lets the last contact win, which makes the car chatter between the
  // two faces of a corner instead of sliding along it.
  let pushX = 0;
  let pushZ = 0;
  let normalX = 0;
  let normalZ = 0;
  let yaw = 0;
  let contacts = 0;

  for (const offset of BODY_OFFSETS) {
    const cx = s.x + nfx * offset;
    const cz = s.z + nfz * offset;
    // A parapet is a wall like any other once the car is up at its level; `s.y` is still the
    // surface from the previous substep, which is the height that decides that.
    const hit = world.buildings.resolveCircle(cx, cz, t.radius) ?? world.decks?.resolveCircle(cx, cz, t.radius, s.y);
    if (!hit) continue;
    contacts++;
    pushX += hit.px;
    pushZ += hit.pz;
    normalX += hit.nx;
    normalZ += hit.nz;
    if (offset !== 0) {
      // A shove on the nose or the tail also turns the car — clip a wall with a front
      // wing and you get pointed away from it. Kept gentle: at full strength every graze
      // becomes a spin, which is much less fun than it sounds.
      //
      // Scaled *by* the lever arm, not divided by it: a contact further from the centre
      // has to turn the car more, not less. The two are the same number for a symmetric
      // three-circle body, but they stop being the same the moment anyone reshapes it.
      const lateral = hit.px * nrx + hit.pz * nrz;
      yaw -= lateral * (offset / BODY_SPREAD) * t.yawOnImpact;
    }
  }

  if (contacts > 0) {
    s.x += pushX / contacts;
    s.z += pushZ / contacts;
    s.heading += clamp(yaw / contacts, -0.06, 0.06);

    const nLen = Math.hypot(normalX, normalZ);
    if (nLen > 1e-6) {
      const hx = normalX / nLen;
      const hz = normalZ / nLen;
      const into = s.vx * hx + s.vz * hz;
      if (into < 0) {
        s.impact = Math.max(s.impact, -into);
        // Remove the velocity going into the wall and keep a slice of it as bounce.
        s.vx -= into * (1 + t.wallRestitution) * hx;
        s.vz -= into * (1 + t.wallRestitution) * hz;
        // Then scrub what's left, so scraping down a street bleeds speed. This has to be
        // scaled by dt: a flat per-step multiplier runs 120 times a second and turned the
        // lightest kerb graze into a dead stop.
        const scrub = Math.exp(-t.wallFriction * dt);
        s.vx *= scrub;
        s.vz *= scrub;
      }
    }
  }

  if (world.trains?.length) resolveTrains(s, world.trains, t, dt);

  s.speed = Math.hypot(s.vx, s.vz);
  // Sampled after the wall resolution, or a car shoved out of a facade on a slope reports
  // the height of the wall it was inside rather than the street it ended up on.
  // `s.y` still holds the surface from the previous substep, which is what tells a deck
  // whether this car is on it or driving under it — see `DeckIndex.sample`.
  // The deck lookup is asked from the surface, not from the top of a kerb; the two are a few
  // centimetres apart and a deck is metres, but there is no reason to make it guess.
  const lift = s.pavement ? KERB_HEIGHT : 0;
  s.y = surfaceHeight(s.x, s.z, world, s.y - lift) + lift;
}

/**
 * Rolling stock, after the walls: a box per car, and unlike a wall it can be moving.
 *
 * The response is the wall's, measured against the train's velocity rather than against a
 * standing wall. Driven into the side of a train standing at a platform, a car stops as it
 * would at a building; standing on a crossing as one comes through, it is given the train's
 * speed along the push and shoved clear, which is the one honest outcome and the reason
 * nobody does it twice. A train never slows for anything: it is on a timetable every client
 * shares, and a train one player had stopped would be somewhere else for everyone else.
 *
 * Only a car at about the height of the rails meets one, so a car on a viaduct over the line,
 * or on the street under a railway bridge, drives on.
 */
function resolveTrains(s: CarState, trains: TrainBody[], t: CarTuning, dt: number): void {
  const nfx = -Math.sin(s.heading);
  const nfz = -Math.cos(s.heading);
  let pushX = 0;
  let pushZ = 0;
  let normalX = 0;
  let normalZ = 0;
  let trainVx = 0;
  let trainVz = 0;
  let contacts = 0;
  for (const offset of BODY_OFFSETS) {
    const cx = s.x + nfx * offset;
    const cz = s.z + nfz * offset;
    for (const b of trains) {
      if (s.y < b.y - TRAIN_REACH_BELOW || s.y > b.y + b.height) continue;
      const rx = cx - b.x;
      const rz = cz - b.z;
      const reach = b.halfLength + t.radius;
      if (rx * rx + rz * rz > reach * reach + b.halfWidth * b.halfWidth) continue;
      // Into the box's own axes: `u` along the car, `w` across it.
      const lu = rx * b.ux + rz * b.uz;
      const lw = -rx * b.uz + rz * b.ux;
      const qu = clamp(lu, -b.halfLength, b.halfLength);
      const qw = clamp(lw, -b.halfWidth, b.halfWidth);
      let du = lu - qu;
      let dw = lw - qw;
      let dist = Math.hypot(du, dw);
      let depth: number;
      if (dist > 1e-6) {
        if (dist >= t.radius) continue;
        depth = t.radius - dist;
      } else {
        // The centre is inside the box — a train arriving on top of a parked car. Out through
        // the nearer long side, which is always the right way off a track.
        dw = lw >= 0 ? 1 : -1;
        du = 0;
        dist = 1;
        depth = b.halfWidth - Math.abs(lw) + t.radius;
      }
      let nu = du / dist;
      let nw = dw / dist;
      // Met end-on by a train moving into it, a car is thrown aside rather than pushed down
      // the line ahead of the train for the rest of its journey.
      const closing = (b.vx * b.ux + b.vz * b.uz) * nu;
      if (Math.abs(nu) > Math.abs(nw) && closing > 1) {
        const side = lw >= 0 ? 1 : -1;
        const len = Math.hypot(nu, 1.7);
        nu /= len;
        nw = (1.7 * side) / len;
      }
      const nx = nu * b.ux - nw * b.uz;
      const nz = nu * b.uz + nw * b.ux;
      pushX += nx * depth;
      pushZ += nz * depth;
      normalX += nx;
      normalZ += nz;
      trainVx += b.vx;
      trainVz += b.vz;
      contacts++;
    }
  }
  if (!contacts) return;
  s.x += pushX / contacts;
  s.z += pushZ / contacts;
  const nLen = Math.hypot(normalX, normalZ);
  if (nLen < 1e-6) return;
  const hx = normalX / nLen;
  const hz = normalZ / nLen;
  const into = (s.vx - trainVx / contacts) * hx + (s.vz - trainVz / contacts) * hz;
  if (into >= 0) return;
  s.impact = Math.max(s.impact, -into);
  s.vx -= into * (1 + t.wallRestitution) * hx;
  s.vz -= into * (1 + t.wallRestitution) * hz;
  const scrub = Math.exp(-t.wallFriction * dt);
  s.vx *= scrub;
  s.vz *= scrub;
}

/** How far under the rails a car can be and still meet a train — a dip in a level crossing. */
const TRAIN_REACH_BELOW = 1.2;

/**
 * The height of whatever the car is driving on: the deck if it is on one, the ground if not.
 *
 * Exported because `Game.setSpawn` needs the same answer: it places the car before the first
 * step runs, and a grid on a bridge would otherwise spend the countdown in the water. It has
 * no previous height to offer, which is right — a car being placed is arriving from nowhere,
 * so whatever is on top at that point is what it lands on.
 */
export function surfaceHeight(x: number, z: number, world: CarWorld, fromY?: number): number {
  const deck = world.decks?.sample(x, z, fromY);
  if (deck) return deck.y;
  return world.ground ? world.ground.heightAt(x, z) : 0;
}

function clamp(n: number, lo: number, hi: number): number {
  return n < lo ? lo : n > hi ? hi : n;
}

/** Signed forward speed — negative when reversing. Used by the HUD's speedometer. */
export function forwardSpeed(s: CarState): number {
  const fx = -Math.sin(s.heading);
  const fz = -Math.cos(s.heading);
  return s.vx * fx + s.vz * fz;
}
